import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { mutate } from 'swr';
import { useParams } from 'react-router-dom';
import { Form, Button, Card, Alert, Modal, Spinner, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { trans as dbtrans } from '../../../helpers/laudos/trans';
import CustomFileInput from '../../../components/CustomFileInput';
import { liberarLaudoByPac } from '../../../services/prontuario';
import useUploadFiles from '../../../hooks/useUploadFiles';
import useViewFiles from '../../../hooks/useViewFiles';
import FileCarousel from '../../../components/laudos/FileCarousel';
import FontScaleButtonGroup from '../../../components/FontScaleButtonGroup';
import generateTransPDF from './generateTransPDF';
import Footer from '../../../components/laudos/Footer';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import { getPrefix } from '../../../util/utils';
import { updateTransById } from '../../../services/trans';
import useTrans from '../../../hooks/useTrans';
import ModalTrans from './ModalTrans';
import useIsMedicoExterno from '../../../hooks/useIsMedicoExterno';

const TRANS_EXTENSIONS = ['.jpeg', '.jpg', '.webm', '.pdf', '.mp4'];

export default function FormTrans() {
  const { id } = useParams();
  const { data, error, isLoading } = useTrans(id);
  const { profile: medico } = useSelector(state => state.doctor);
  const isMedicoExterno = useIsMedicoExterno();
  const [trans, setTrans] = useState(null);
  const {
    urls,
    setURLs,
    loadingFiles,
    showModal: showFilesModal,
    setShowModal: setShowFilesModal,
    showFiles,
  } = useViewFiles({
    codLaudo: trans?.CODTRANS,
    procedimento: 'trans',
    extensions: TRANS_EXTENSIONS,
    parallel: true,
  });
  const [showModalTrans, setShowModalTrans] = useState(false);
  const [fontScale, setFontScale] = useState(1);
  const [restaurarTrans, setRestaurarTrans] = useState(false);
  const descricaoRef = useRef(null);

  useEffect(() => {
    if (data && data.length > 0)
      setTrans(data[0]);
  }, [data]);

  const {
    files,
    setFiles,
    showUpload,
    setShowUpload,
    isUploading: uploadingFilesLoading,
    confirmUpload,
    handleCloseModal,
  } = useUploadFiles({ atividade: trans });

  const handleGravar = async (event, tipo) => {
    try {
      event.preventDefault();
      const updatedTrans = { ...trans, tipo };
      const res = await updateTransById(updatedTrans);
      if (tipo === 'definitivo') {
        const fatura = {
          codLaudo: updatedTrans.CODTRANS,
          codPac: updatedTrans.CODPAC,
        };

        await liberarLaudoByPac({ fatura });
        toast.success('Laudo salvo e liberado com sucesso!');
        setRestaurarTrans(false);
        mutate(`trans-${id}`);
        return;
      }

      setRestaurarTrans(false);
      toast.success(res.data.message);
      mutate(`trans-${id}`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gravar trans!');
    }
  };

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo de trans!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading || !trans) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  return (
    <>
      <Card style={{ marginTop: 10, marginBottom: 10 }}>
        <LaudoCardHeader title="ECOCARDIOGRAMA TRANSESOFÁGICO" />
        {!isMedicoExterno && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '8px 16px 0 16px',
            }}
          >
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowModalTrans(true)}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <span style={{ marginRight: 6 }}>
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M10 1.5A1.5 1.5 0 0 1 11.5 3v1h-7V3A1.5 1.5 0 0 1 6 1.5h4zm1.5 2A.5.5 0 0 0 11 3V1.5A2.5 2.5 0 0 0 8.5-1h-4A2.5 2.5 0 0 0 2 1.5V3a.5.5 0 0 0-.5.5v10A2.5 2.5 0 0 0 4 16h8a2.5 2.5 0 0 0 2.5-2.5v-10A.5.5 0 0 0 14 3V1.5A1.5 1.5 0 0 0 12.5 0h-4A1.5 1.5 0 0 0 7 1.5V3a.5.5 0 0 0-.5.5v10A1.5 1.5 0 0 0 8 15h4a1.5 1.5 0 0 0 1.5-1.5v-10z" />
                </svg>
              </span>
              Copiar Laudo Anterior
            </Button>
          </div>
        )}
        <Card.Body>
          <Form noValidate>
            <strong className="mr-auto">Nome do Paciente: </strong>
            {`${trans.CODPAC} - ${trans.NOMEPAC}`}
            <br></br>
            <strong className="mr-auto">Data de Nasc: </strong>
            {trans.dataNasc}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Sexo: </strong>
            {trans.SEXOPAC}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Idade: </strong>
            {`${trans.idadePAC} anos`}
            <br></br>
            <strong className="mr-auto">Convênio: </strong>
            {trans.SIGLACONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Cod Trans: </strong>
            {trans.CODTRANS}&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Data do Exame: </strong>
            {trans.dataTrans}
            <br></br>
            <strong className="mr-auto">
              Médico que Realizou: {getPrefix(trans?.CODREALIZOU)}{' '}
            </strong>
            {trans?.Realizou}
            <br></br>
            <strong className="mr-auto">
              Médico que Solicitou: {getPrefix(trans?.CODSOLICITANTE)}{' '}
            </strong>
            {trans?.Solicitou}
            {!isMedicoExterno && (
              <>
                <hr></hr>
                <strong>TRANSESOFÁGICO</strong>
                <Form.Check
                  label="Transesofágico Normal"
                  className="mb-2"
                  name="groupTrans"
                  type="radio"
                  onClick={() => {
                    setTrans({ ...trans, ECOTRANS: dbtrans[0].normal });
                    descricaoRef.current?.focus();
                  }}
                  disabled={trans.HASH}
                />
              </>
            )}
            <Form.Group size="sm" controlId="editDescricao">
              <Form.Label className="font-weight-bold">DESCRIÇÃO</Form.Label>
              <Form.Control
                as="textarea"
                ref={descricaoRef}
                rows={
                  trans.ECOTRANS ? trans.ECOTRANS.split('\n').length + 3 : 2
                }
                readOnly={!!trans.HASH || isMedicoExterno}
                name="ECOTRANS"
                value={trans.ECOTRANS ? trans.ECOTRANS : ''}
                onChange={e => setTrans({ ...trans, ECOTRANS: e.target.value })}
                onKeyDown={e => {
                  if (e.key !== 'F2') return;
                  const posStart = trans.ECOTRANS?.indexOf('---') ?? -1;
                  if (posStart === -1) return;
                  descricaoRef.current?.setSelectionRange(
                    posStart,
                    posStart + 3
                  );
                }}
              />
            </Form.Group>
            {!isMedicoExterno && (
              <FontScaleButtonGroup
                fontScale={fontScale}
                setFontScale={setFontScale}
              />
            )}
            {medico && (
              <>
                <Card className="mt-2" style={{ fontSize: 14 }}>
                  <Row xs={12} sm={6} md={2} lg={2}>
                    <Col sm={6} style={{ align: 'left' }}>
                      <Form.Check
                        className="m-2"
                        type="checkbox"
                        checked={!!restaurarTrans}
                        label="Restaurar Trans"
                        onChange={e => {
                          setRestaurarTrans(e.target.checked);
                        }}
                      />
                    </Col>
                  </Row>
                </Card>
                <Alert
                  show={restaurarTrans}
                  variant="warning"
                  style={{ textAlign: 'right' }}
                >
                  <Alert.Heading>
                    {medico.codMed === trans?.CODREALIZOU ? (
                      <>
                        <span>
                          Dr. {medico.apelido}, confirma a restauração deste
                          Laudo Transesofágico?
                        </span>
                        <Button
                          className="text-right"
                          id="btnUpdateFat"
                          disabled={restaurarTrans ? false : true}
                          onClick={e => handleGravar(e, 'parcial')}
                          variant="warning"
                        >
                          Restaurar Trans
                        </Button>
                      </>
                    ) : (
                      <span>
                        Dr. {medico.apelido}, somente o Médico que realizou o
                        exame pode restaurar este Laudo Transesofágico!
                      </span>
                    )}
                  </Alert.Heading>
                </Alert>
              </>
            )}
            <Footer
              gravarParcial={e => handleGravar(e, 'parcial')}
              gravarDefinitivo={e => handleGravar(e, 'definitivo')}
              imprimirPdfNormal={() =>
                generateTransPDF(trans, 'imprimirNormal', fontScale)
              }
              imprimirPdfTimbrado={() =>
                generateTransPDF(trans, 'imprimirTimbrado', fontScale)
              }
              enviarPorWpp={() =>
                generateTransPDF(trans, 'envioWpp', fontScale)
              }
              verImagens={showFiles}
              abrirUploadModal={() => setShowUpload(true)}
              laudo={trans}
            />
          </Form>
        </Card.Body>
      </Card>

      <Modal show={showUpload} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={true}
            onChange={e => setFiles(Array.from(e.target.files))}
          />
          <ul>
            {files && files.map(file => <li key={file.name}>{file.name}</li>)}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          {uploadingFilesLoading ? (
            <Spinner animation="border" variant="dark" />
          ) : (
            <Button
              onClick={confirmUpload}
              disabled={uploadingFilesLoading || files?.length === 0}
            >
              Confirmar
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={showFilesModal}
        onHide={() => setShowFilesModal(false)}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Imagens</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFiles ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <Spinner animation="border" variant="dark" size="lg" />
            </div>
          ) : (
            <FileCarousel urls={urls} setURLs={setURLs} />
          )}
        </Modal.Body>
      </Modal>

      <ModalTrans
        show={showModalTrans}
        onHide={() => setShowModalTrans(false)}
        codPac={trans?.CODPAC} // Código do paciente do laudo atual
        onCopy={data => {
          if (!data) return;

          setTrans(prevTrans => ({
            ...prevTrans,              // Mantém os dados e IDs do laudo atual
            ECOTRANS: data?.ECOTRANS, // Copia a conclusão ou outros campos necessários
          }));

          toast.success('Laudo Transesofágico copiado com sucesso!');
          setShowModalTrans(false);
        }}
      />
    </>
  );
}
